import { useState } from 'react';
import { ActionIcon, ScrollArea, Tooltip } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconStar, IconStarFilled } from '@tabler/icons-react';
import styles from './TransferList.module.css';

export interface TransferListItem {
  id: string;
  label: string;
}

export interface TransferListProps {
  availableItems: TransferListItem[];
  selectedIds: string[];
  defaultId: string | null;
  onChange: ( selectedIds: string[], defaultId: string | null ) => void;
  labels: { available: string; selected: string; defaultTooltip: string };
  disabled?: boolean;
}

const TransferList = ({ availableItems, selectedIds, defaultId, onChange, labels, disabled }: TransferListProps ) => {
  const [ highlightedAvailable, setHighlightedAvailable ] = useState<Set<string>>( new Set());
  const [ highlightedSelected, setHighlightedSelected ] = useState<Set<string>>( new Set());

  const selectedSet = new Set( selectedIds );
  const available = availableItems.filter( item => !selectedSet.has( item.id ));
  const selected = availableItems.filter( item => selectedSet.has( item.id ));

  const toggleHighlight = ( id: string, set: Set<string>, setter: ( s: Set<string> ) => void ) => {
    const next = new Set( set );
    if ( next.has( id )) {
      next.delete( id );
    } else {
      next.add( id );
    }
    setter( next );
  };

  const handleMoveRight = () => {
    if ( disabled ) return;
    const toMove = Array.from( highlightedAvailable );
    if ( toMove.length === 0 ) return;

    const newSelectedIds = [ ...selectedIds, ...toMove ];
    const newDefault = defaultId ?? toMove[0];
    setHighlightedAvailable( new Set());
    onChange( newSelectedIds, newDefault );
  };

  const handleMoveLeft = () => {
    if ( disabled ) return;
    const toRemove = new Set( highlightedSelected );
    if ( toRemove.size === 0 ) return;

    const newSelectedIds = selectedIds.filter( id => !toRemove.has( id ));
    let newDefault = defaultId;
    if ( defaultId && toRemove.has( defaultId )) {
      newDefault = newSelectedIds.length > 0 ? newSelectedIds[0] : null;
    }
    setHighlightedSelected( new Set());
    onChange( newSelectedIds, newDefault );
  };

  const handleSetDefault = ( id: string ) => {
    if ( disabled ) return;
    onChange( selectedIds, id );
  };

  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={styles.columnHeader}>{labels.available}</div>
        <ScrollArea className={styles.scrollArea} h={180}>
          {available.length === 0 ? (
            <div className={styles.emptyText}>-</div>
          ) : (
            available.map( item => (
              <div
                key={item.id}
                className={`${styles.item} ${highlightedAvailable.has( item.id ) ? styles.itemHighlighted : ''}`}
                onClick={() => toggleHighlight( item.id, highlightedAvailable, setHighlightedAvailable )}
              >
                <span>{item.label}</span>
              </div>
            ))
          )}
        </ScrollArea>
      </div>

      <div className={styles.controls}>
        <ActionIcon
          variant="light"
          size="sm"
          onClick={handleMoveRight}
          disabled={disabled || highlightedAvailable.size === 0}
        >
          <IconChevronRight size={14} />
        </ActionIcon>
        <ActionIcon
          variant="light"
          size="sm"
          onClick={handleMoveLeft}
          disabled={disabled || highlightedSelected.size === 0}
        >
          <IconChevronLeft size={14} />
        </ActionIcon>
      </div>

      <div className={styles.column}>
        <div className={styles.columnHeader}>{labels.selected}</div>
        <ScrollArea className={styles.scrollArea} h={180}>
          {selected.length === 0 ? (
            <div className={styles.emptyText}>-</div>
          ) : (
            selected.map( item => (
              <div
                key={item.id}
                className={`${styles.item} ${highlightedSelected.has( item.id ) ? styles.itemHighlighted : ''}`}
                onClick={() => toggleHighlight( item.id, highlightedSelected, setHighlightedSelected )}
              >
                <span>{item.label}</span>
                <Tooltip label={labels.defaultTooltip} withArrow position="left">
                  {defaultId === item.id ? (
                    <IconStarFilled size={14} className={styles.defaultStar} />
                  ) : (
                    <IconStar
                      size={14}
                      className={styles.emptyStar}
                      onClick={( e ) => { e.stopPropagation(); handleSetDefault( item.id ); }}
                    />
                  )}
                </Tooltip>
              </div>
            ))
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default TransferList;
